import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Alert,
} from "@mui/material";
import Scrollbar from "../components/scrollbar";
import { get } from "src/utils/networkUtils";
import SeasonsTable from "src/components/seasons/SeasonsTable";
import SeasonDialog from "src/components/seasons/SeasonDialog";
import useWithUrlTenant from "src/hooks/useTenant";

export default function SeasonsPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState(null);
  const [openSeasonDialog, setOpenSeasonDialog] = useState(false);
  const [editSeason, setEditSeason] = useState(null);
  const withTenant = useWithUrlTenant();

  const fetchSeasons = useCallback(() => {
    setLoading(true);
    get(withTenant(`/admin/seasons`))
      .then((response) => {
        setLoading(false);
        setRows(response.data || []);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching seasons:", error);
      });
  }, [withTenant]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  const handleOpenCreateDialog = () => {
    setEditSeason(null);
    setOpenSeasonDialog(true);
  };

  const handleOpenEditDialog = (season) => {
    setEditSeason(season);
    setOpenSeasonDialog(true);
  };

  const handleCloseDialog = () => {
    setEditSeason(null);
    setOpenSeasonDialog(false);
  };

  const handleSeasonSuccess = () => {
    setOpenSeasonDialog(false);
    setEditSeason(null);
    fetchSeasons();
  };

  return (
    <>
      <Helmet>
        <title>Seasons</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4" gutterBottom>
            Seasons
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenCreateDialog}
          >
            Create Season
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong>
          </Typography>
          <Typography
            variant="body2"
            component="ul"
            sx={{ mt: 1, mb: 0, pl: 2 }}
          >
            <Typography component="li" variant="body2">
              Scoreables (teams & players), scoreable season stats, fixtures,
              match stats are fetched for current season only.
            </Typography>
            <Typography component="li" variant="body2">
              The app fetches user cards for current season only.
            </Typography>
            <Typography component="li" variant="body2">
              Please set it carefully.
            </Typography>
          </Typography>
        </Alert>

        <Card>
          {rows === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <SeasonsTable
                seasons={rows}
                onEditSeason={handleOpenEditDialog}
              />
            </Scrollbar>
          )}
        </Card>

        <SeasonDialog
          open={openSeasonDialog}
          season={editSeason}
          onClose={handleCloseDialog}
          onSuccess={handleSeasonSuccess}
        />
      </Container>
    </>
  );
}
